import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, inject, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { PromptResearchService, PromptResearch } from '../../../services/prompt-research.service';
import { PromptResearchDialogComponent } from '../../../dialogs/prompt-research-dialog/prompt-research-dialog.component';
import { CommonConfirmDialogComponent } from '../../../dialogs/common-confirm-dialog/common-confirm-dialog.component';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { GenericCommonTabsComponent } from '../../components/template-ai-common/generic-common-tabs/generic-common-tabs.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-generic-prompt-research',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule
  ],
  templateUrl: './generic-prompt-research.component.html',
  styleUrl: './generic-prompt-research.component.scss'
})
export class GenericPromptResearchComponent implements OnInit, OnDestroy {
  @Input() selectedEntities: any[] = [];

  private promptResearchService = inject(PromptResearchService);
  private templateCommonService = inject(GenericTemplateCommonService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private cdr = inject(ChangeDetectorRef);
  
  // Inject parent component for tab navigation
  constructor(@Optional() @SkipSelf() private parentTabs?: GenericCommonTabsComponent) {}

  researches: PromptResearch[] = [];
  loading = false;
  error: string | null = null;
  deletingResearchId: string | null = null;
  
  // Current project ID
  currentProjectId: string | null = null;
  
  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    // Get current project ID from template service
    const projectData = this.templateCommonService.getCurrentProjectData();
    if (projectData?.id) {
      this.currentProjectId = projectData.id;
      this.loadResearches();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadResearches(): void {
    this.loading = true;
    this.error = null;

    const sub = this.promptResearchService.getResearchList(this.currentProjectId || undefined).subscribe({
      next: (response) => {
        this.researches = response.researches;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading researches:', err);
        this.error = err.error?.detail || 'Failed to load prompt research history';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });

    this.subscriptions.push(sub);
  }

  openNewResearchDialog(): void {
    // Get project data for primary_country and language (so prompts/data come in project language)
    const projectData = this.templateCommonService.getCurrentProjectData();
    const primary_country = projectData?.primary_country || 'Global';
    const language = projectData?.language ?? 'English';
    
    const dialogRef = this.dialog.open(PromptResearchDialogComponent, {
      width: '80%',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'prompt-research-dialog-panel',
      data: {
        projectId: this.currentProjectId,
        mode: 'create',
        primary_country: primary_country,
        language: language
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'success') {
        this.loadResearches();
      }
    });
  }

  openResearchViewDialog(research: PromptResearch, event?: Event): void {
    // Prevent event bubbling to avoid double opening
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Validate research data
    if (!research || !research.id) {
      console.error('Invalid research data provided');
      return;
    }
    
    // Ensure mode is explicitly set to 'view'
    const dialogRef = this.dialog.open(PromptResearchDialogComponent, {
      width: '80%',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'prompt-research-dialog-panel',
      disableClose: true,
      data: {
        researchId: research.id,
        research: research,
        mode: 'view' as const
      },
    });
  }

  onRowClick(research: PromptResearch, event: Event): void {
    // Don't trigger row click if clicking on buttons or in actions cell
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('.actions-cell') || target.closest('mat-icon-button')) {
      return;
    }
    
    // Only open dialog if clicking directly on the row (not on buttons)
    this.openResearchViewDialog(research, event);
  }

  exportResearch(research: PromptResearch, event: Event): void {
    event.stopPropagation();

    const sub = this.promptResearchService.exportResearch(research.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `prompt_research_${research.id}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Research exported successfully', 'Close', { duration: 2000 });
      },
      error: (err) => {
        console.error('Error exporting research:', err);
        this.snackBar.open('Failed to export research', 'Close', { duration: 3000 });
      }
    });

    this.subscriptions.push(sub);
  }

  deleteResearch(research: PromptResearch, event: Event): void {
    event.stopPropagation();
    if (this.deletingResearchId) return;

    const ref = this.dialog.open(CommonConfirmDialogComponent, {
      width: '450px',
      disableClose: true,
      data: {
        title: 'Delete Research?',
        description: `This will permanently delete "${research.research_name}" and all its prompts. This action cannot be undone.`,
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.deletingResearchId = research.id;
      const sub = this.promptResearchService.deleteResearch(research.id).subscribe({
        next: () => {
          this.researches = this.researches.filter(r => r.id !== research.id);
          this.deletingResearchId = null;
          this.snackBar.open('Research deleted', 'Close', { duration: 2000 });
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.deletingResearchId = null;
          this.snackBar.open(err?.error?.detail || 'Failed to delete research', 'Close', { duration: 3000 });
          this.cdr.detectChanges();
        }
      });
      this.subscriptions.push(sub);
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  // Tab navigation methods
  getVisibleTabs(): any[] {
    if (this.parentTabs && typeof (this.parentTabs as any).getVisibleTabs === 'function') {
      return (this.parentTabs as any).getVisibleTabs();
    }
    // Fallback: return common tabs
    return [
      { id: 'overview', name: 'Overview', icon: 'dashboard' },
      { id: 'ai-citations', name: 'AI Citations', icon: 'link' },
      { id: 'category-analysis', name: 'Category Analysis', icon: 'category' },
      { id: 'consumer-insights', name: 'Consumer Insights', icon: 'psychology' },
      { id: 'sentiment-tracking', name: 'Sentiment Tracking', icon: 'sentiment_satisfied' },
      { id: 'ai-content-index', name: 'AI Content Index', icon: 'article' },
      { id: 'prompt-research', name: 'Prompt Creation', icon: 'search' },
      { id: 'content-creation', name: 'Content Creation', icon: 'create' },
      { id: 'prompt-tracking', name: 'Prompt Tracking', icon: 'track_changes' }
    ];
  }

  getSubTabs(parentTabId: string): any[] {
    if (this.parentTabs && typeof (this.parentTabs as any).getSubTabs === 'function') {
      return (this.parentTabs as any).getSubTabs(parentTabId);
    }
    return [];
  }

  openTab(tabId: string): void {
    if (this.parentTabs && typeof (this.parentTabs as any).openTab === 'function') {
      (this.parentTabs as any).openTab(tabId);
    }
  }

  openSubTab(parentTabId: string, subTabId: string): void {
    if (this.parentTabs && typeof (this.parentTabs as any).openSubTab === 'function') {
      (this.parentTabs as any).openSubTab(parentTabId, subTabId);
    }
  }
}
